// junkStats.js — pure helpers for the Junk tracker.
//
// "Junk" is defined as anything routed to the `snacks` slot in calorieLog,
// which is the slot labelled "Junk Food" in the UI. The auto-classifier
// in CalorieCounter (isJunkFood) decides slot routing at log time, so we
// can treat slot membership as ground truth and skip re-classifying here.
//
// The harm-reduction model: every day has a `pct` = junk_kcal / total_kcal.
// A day is "clean" when pct < threshold (default 20%). Streaks count
// consecutive clean days with a single rolling-week freeze allowed (so a
// wedding meal doesn't wipe weeks of progress).

export const DEFAULT_JUNK_THRESHOLD = 20;
export const JUNK_SLOT = "snacks";

const localDateStr = (d = new Date()) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const dayOffset = (n) => {
  const d = new Date(); d.setDate(d.getDate() + n); return localDateStr(d);
};

const sumKcal = (arr) => (Array.isArray(arr) ? arr : []).reduce((s, m) => s + (+m?.calories || 0), 0);

// ───────────────────────────────────────────────────────────────────────
// computeDayJunk — single-day junk %
// Returns { totalKcal, junkKcal, pct, isClean, hasMeals }.
// `hasMeals` is false when the day has zero logged calories — we don't
// punish empty days as "dirty" but we also don't credit them as clean.
// ───────────────────────────────────────────────────────────────────────
export function computeDayJunk(logEntry, threshold = DEFAULT_JUNK_THRESHOLD) {
  const meals = logEntry?.meals || {};
  let totalKcal = 0;
  let junkKcal = 0;
  for (const slot of Object.keys(meals)) {
    const kc = sumKcal(meals[slot]);
    totalKcal += kc;
    if (slot === JUNK_SLOT) junkKcal += kc;
  }
  const hasMeals = totalKcal > 0;
  const pct = hasMeals ? Math.round((junkKcal / totalKcal) * 1000) / 10 : 0;
  return { totalKcal, junkKcal, pct, isClean: hasMeals && pct < threshold, hasMeals };
}

// ───────────────────────────────────────────────────────────────────────
// computeJunkStreak — current and best clean-day streak
// One "freeze" per rolling 7 days: a dirty day inside a streak burns the
// freeze instead of resetting, IF no freeze has been spent in the prior
// 7 days. Empty/no-meals days neither extend nor break the streak.
// ───────────────────────────────────────────────────────────────────────
export function computeJunkStreak(calorieLog, threshold = DEFAULT_JUNK_THRESHOLD, todayStr = null, manualFreezes = []) {
  const today = todayStr || localDateStr();
  // Walk backwards from yesterday (today's still in progress and not
  // settled — we use computeTodayEarnedCheat for the live signal instead).
  const yest = new Date(`${today}T00:00:00`); yest.setDate(yest.getDate() - 1);
  let cursor = yest;
  let current = 0;
  let best = 0;
  let runLen = 0;
  const freezeSet = new Set(manualFreezes || []);
  let firstStreakBroken = false;
  let scanLimit = 365; // walk at most a year back
  let dirtyBreakDate = null;

  while (scanLimit-- > 0) {
    const ds = localDateStr(cursor);
    const day = computeDayJunk(calorieLog[ds], threshold);

    if (!day.hasMeals) {
      // skip empty days
    } else if (day.isClean) {
      runLen += 1;
      if (!firstStreakBroken) current += 1;
      if (runLen > best) best = runLen;
    } else {
      // Dirty day. Previously auto-protected by a rolling 7-day freeze,
      // which surprised users (the badge fired without their input).
      // Freezes are now manual: a dirty day only survives if the user
      // tapped the ❄ badge to flag it.
      if (freezeSet.has(ds) && !firstStreakBroken) {
        // protect streak — runLen continues, no credit for the dirty day
      } else {
        if (!firstStreakBroken) {
          firstStreakBroken = true;
          dirtyBreakDate = ds;
        }
        runLen = 0;
      }
    }
    cursor.setDate(cursor.getDate() - 1);
    if (firstStreakBroken && scanLimit < 360 - 60) break;
  }
  // freezeUsedRecently: did the user use a manual freeze in the last 7 days?
  const cutoffMs = Date.now() - 7 * 86400000;
  const freezeUsedRecently = (manualFreezes || []).some(d => Date.parse(`${d}T00:00:00`) >= cutoffMs);
  return {
    current,
    best,
    freezeUsedRecently,
    brokenOn: dirtyBreakDate,
  };
}

// ───────────────────────────────────────────────────────────────────────
// computeTodayEarnedCheat — has the user "earned" a cheat meal today?
// Eligible when streak ≥ 3 AND today's junk% is still under (threshold/2)
// so a moderate cheat won't push past threshold.
// ───────────────────────────────────────────────────────────────────────
export function computeTodayEarnedCheat(calorieLog, threshold = DEFAULT_JUNK_THRESHOLD, todayStr = null, claimedToday = false) {
  const today = todayStr || localDateStr();
  const streak = computeJunkStreak(calorieLog, threshold, today);
  const todayDay = computeDayJunk(calorieLog[today], threshold);
  // The chip appears once you're partway through the day (have logged
  // anything) and still well below the line. If you've already claimed
  // it today, hide the chip.
  const eligible = !claimedToday &&
    streak.current >= 3 &&
    todayDay.hasMeals &&
    todayDay.pct < threshold / 2;
  // Suggested cheat-meal budget = remaining junk % runway × today's total ×
  // 0.6 (leaves a buffer so the day still ends clean).
  const remainingPct = threshold - todayDay.pct;
  const suggestedKcal = Math.max(0, Math.round(todayDay.totalKcal * (remainingPct / 100) * 0.6));
  return {
    eligible,
    streak: streak.current,
    todayPct: todayDay.pct,
    threshold,
    suggestedKcal,
    reason: eligible
      ? `${streak.current}-day clean streak + today at ${todayDay.pct}% junk`
      : null,
  };
}

// ───────────────────────────────────────────────────────────────────────
// computeRangeJunkStats — aggregates over the last N days
// Returns:
//  - daily series (each day with pct + isClean)
//  - cleanDays count
//  - avgPct
//  - wow (this week vs last 7d)
//  - byWeek (8 weeks of Mon-Sun junk-%)
//  - byWeekday (junk-% averaged per Mon..Sun)
//  - topOffenders (top 5 by total kcal and top 5 by frequency)
// ───────────────────────────────────────────────────────────────────────
export function computeRangeJunkStats(calorieLog, days = 30, threshold = DEFAULT_JUNK_THRESHOLD) {
  const series = [];
  for (let i = days - 1; i >= 0; i--) {
    const ds = dayOffset(-i);
    const d = computeDayJunk(calorieLog[ds], threshold);
    series.push({ date: ds, ...d });
  }
  const logged = series.filter(d => d.hasMeals);
  const cleanDays = logged.filter(d => d.isClean).length;
  const avgPct = logged.length
    ? +(logged.reduce((s, d) => s + d.pct, 0) / logged.length).toFixed(1)
    : 0;

  // Week-over-week (last 7 vs the 7 before)
  const last7   = series.slice(-7).filter(d => d.hasMeals);
  const prev7   = series.slice(-14, -7).filter(d => d.hasMeals);
  const wAvg = (arr) => arr.length ? +(arr.reduce((s, d) => s + d.pct, 0) / arr.length).toFixed(1) : null;
  const wow = { thisWeek: wAvg(last7), lastWeek: wAvg(prev7) };
  wow.delta = wow.thisWeek != null && wow.lastWeek != null ? +(wow.thisWeek - wow.lastWeek).toFixed(1) : null;

  // 8-week trend (Mon-start) — independent of `days` param so the chart
  // always shows 8 buckets regardless of selected range.
  const startOfWeek = (ts) => {
    const d = new Date(ts); d.setHours(0,0,0,0);
    const dow = d.getDay() || 7; // 1..7 with Mon=1
    d.setDate(d.getDate() - (dow - 1));
    return d.getTime();
  };
  const thisMon = startOfWeek(Date.now());
  const byWeek = [];
  for (let i = 7; i >= 0; i--) {
    const wsTs = thisMon - i * 7 * 86400000;
    const ws = localDateStr(new Date(wsTs));
    let tot = 0, junk = 0, logCount = 0, cleanCount = 0;
    for (let dd = 0; dd < 7; dd++) {
      const d2 = new Date(wsTs + dd * 86400000);
      const ds = localDateStr(d2);
      const day = computeDayJunk(calorieLog[ds], threshold);
      if (day.hasMeals) { tot += day.totalKcal; junk += day.junkKcal; logCount++; if (day.isClean) cleanCount++; }
    }
    byWeek.push({
      weekStart: ws,
      junkKcal: junk,
      totalKcal: tot,
      pct: tot > 0 ? +((junk / tot) * 100).toFixed(1) : null,
      cleanDays: cleanCount,
      loggedDays: logCount,
    });
  }

  // Day-of-week heatmap — junk% averaged per Mon..Sun across the full range
  const dowBuckets = [[], [], [], [], [], [], []]; // Mon..Sun
  for (const d of logged) {
    const dt = new Date(`${d.date}T12:00:00`);
    const idx = (dt.getDay() + 6) % 7; // shift Sun=0 to Mon=0
    dowBuckets[idx].push(d.pct);
  }
  const byWeekday = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((label, i) => ({
    label,
    avgPct: dowBuckets[i].length ? +(dowBuckets[i].reduce((s, x) => s + x, 0) / dowBuckets[i].length).toFixed(1) : null,
    samples: dowBuckets[i].length,
  }));

  // Top offenders — walk junk slot items in range
  const byKcal = new Map();
  const byFreq = new Map();
  for (let i = days - 1; i >= 0; i--) {
    const ds = dayOffset(-i);
    const items = (calorieLog[ds]?.meals?.[JUNK_SLOT] || []);
    for (const m of items) {
      const name = (m.name || "Unknown").trim();
      const kc = +m.calories || 0;
      byKcal.set(name, (byKcal.get(name) || 0) + kc);
      byFreq.set(name, (byFreq.get(name) || 0) + 1);
    }
  }
  const topByKcal = [...byKcal.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5).map(([name, kcal]) => ({ name, kcal: Math.round(kcal) }));
  const topByFreq = [...byFreq.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5).map(([name, count]) => ({ name, count }));

  return {
    days,
    threshold,
    series,
    loggedDays: logged.length,
    cleanDays,
    avgPct,
    wow,
    byWeek,
    byWeekday,
    topByKcal,
    topByFreq,
  };
}

// ───────────────────────────────────────────────────────────────────────
// Persistence helpers for the cheat-meal claim
// ───────────────────────────────────────────────────────────────────────
const CHEAT_CLAIM_KEY = "junkCheatClaims";
export function loadCheatClaims() {
  try { return JSON.parse(localStorage.getItem(CHEAT_CLAIM_KEY) || "{}"); } catch { return {}; }
}
export function isCheatClaimedToday(todayStr = null) {
  const t = todayStr || localDateStr();
  return !!loadCheatClaims()[t];
}
export function claimCheatToday(todayStr = null) {
  const t = todayStr || localDateStr();
  const claims = loadCheatClaims();
  claims[t] = Date.now();
  try { localStorage.setItem(CHEAT_CLAIM_KEY, JSON.stringify(claims)); } catch {}
  return claims;
}
